/**
 * The invitation's proof: one short example session, ask to workbook, in the
 * landing page's vocabulary. It plays once when the dialog opens: the ask, the
 * question the skill always puts before a run (processing is charged, so it
 * waits for a yes), the run rule drawing across the extraction, the workbook.
 * Reduced motion shows the session at rest. The figures are illustrative and
 * labelled as an example; the session names no price because the skill only
 * reports rates Contract Analyzer supplies and never estimates a cost.
 */
import { CheckIcon, FileSpreadsheetIcon, TerminalIcon } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

/** When each line lands, in milliseconds after the dialog opens. */
const SCHEDULE = [150, 900, 1700, 2300, 4200] as const;
const LAST_STEP = SCHEDULE.length - 1;
const APPROVE_STEP = 2;
const RUN_STEP = 3;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Plays the session once per mount; a reader who prefers reduced motion starts at rest. */
function useSessionStep(): number {
  const [atRest] = useState(prefersReducedMotion);
  const [step, setStep] = useState(atRest ? LAST_STEP : -1);
  useEffect(() => {
    if (atRest) return;
    const timers = SCHEDULE.map((delay, index) =>
      window.setTimeout(() => {
        setStep(index);
      }, delay),
    );
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [atRest]);
  return step;
}

function Line({
  step,
  current,
  speaker,
  children,
}: {
  readonly step: number;
  readonly current: number;
  readonly speaker: 'You' | 'Agent' | 'Run';
  readonly children: ReactNode;
}) {
  return (
    <li
      className={cn(
        'grid grid-cols-[3rem_minmax(0,1fr)] items-baseline gap-x-2',
        current < step ? 'opacity-0' : 'animate-ca-enter-up-6',
      )}
    >
      <span className="text-eyebrow text-muted-foreground">
        {speaker === 'Run' ? (
          <>
            <TerminalIcon aria-hidden="true" className="inline size-3.5" />
            <span className="sr-only">Run</span>
          </>
        ) : (
          speaker
        )}
      </span>
      <div className="min-w-0">{children}</div>
    </li>
  );
}

export function AgentSessionPreview() {
  const step = useSessionStep();
  return (
    <figure
      aria-label="Example session in Claude Code"
      // shrink-0: the dialog body is a scrolling flex column, which would otherwise
      // squeeze the clipped card down to its first line on a phone.
      className="shrink-0 overflow-hidden rounded-lg bg-background ring-1 ring-foreground/10"
    >
      <figcaption className="flex h-8 items-center justify-between border-b border-foreground/10 bg-muted/60 px-3">
        <span className="text-eyebrow text-foreground">Claude Code</span>
        <span className="text-micro text-muted-foreground">Example</span>
      </figcaption>
      <ol className="flex flex-col gap-3 px-3 py-3.5 text-sm">
        <Line current={step} speaker="You" step={0}>
          <p className="font-normal leading-snug text-foreground">
            Pull change of control and notice from the contracts in ./dataroom.
          </p>
        </Line>
        <Line current={step} speaker="Agent" step={1}>
          <p className="font-light leading-snug text-muted-foreground">
            <span className="font-normal text-foreground">48 contracts, 2 fields.</span> Processing is
            charged to the engagement. Run it?
          </p>
        </Line>
        <Line current={step} speaker="You" step={APPROVE_STEP}>
          <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 font-bold tracking-tight text-foreground ring-1 ring-foreground/20">
            <CheckIcon aria-hidden="true" className={cn('size-3.5', step >= APPROVE_STEP && 'animate-ca-pop-in')} />
            Yes, run it
          </span>
        </Line>
        <Line current={step} speaker="Run" step={RUN_STEP}>
          <div className="flex items-center gap-3">
            <div className="h-[2px] flex-1 overflow-hidden rounded-full bg-foreground/10">
              <div aria-hidden="true" className={cn('eyp-gradient-5 h-full', step >= RUN_STEP && 'landing-run-rule')} />
            </div>
            <span className="text-micro tabular-nums text-muted-foreground">48 of 48 documents</span>
          </div>
        </Line>
        <Line current={step} speaker="Agent" step={LAST_STEP}>
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <FileSpreadsheetIcon aria-hidden="true" className="size-4 text-status-success" />
            <span className="font-bold tracking-tight text-foreground">result.xlsx</span>
            <span className="text-xs font-light text-muted-foreground">every value linked to its page</span>
          </span>
        </Line>
      </ol>
    </figure>
  );
}
